import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Sparkles, 
  ArrowRight, 
  Calendar, 
  GraduationCap, 
  BarChart3,
  DollarSign,
  HelpCircle,
  TrendingDown,
  CheckCircle,
  Users,
  Star,
  Building2,
  PartyPopper,
  Truck,
  MapPin,
  Loader2,
  TreePine
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { toast } from 'sonner';

// Testimonials data
const artisanTestimonials = [
  {
    quote: "Before AMS Compass, I was guessing at prices and losing money at every event. Now I know exactly what to charge and which events are worth my time. My profit increased 40% in just 3 months.",
    author: "Sarah M.",
    role: "Jewelry Designer",
    metric: "+40% Profit"
  },
  {
    quote: "I used to spend hours on spreadsheets trying to figure out if an event was profitable. AMS Compass does it for me in seconds. I finally have time to create instead of calculate.",
    author: "Marcus T.",
    role: "Woodworker",
    metric: "10+ hrs saved/week"
  },
  {
    quote: "Planning my entire year of events in advance changed everything. No more last-minute scrambling or wondering if a show is worth it. I now know exactly where I'll be, what inventory I need, and my projected income for the whole year.",
    author: "Elena R.",
    role: "Ceramics Artist",
    metric: "12-month calendar planned"
  },
];

const eventHostTestimonials = [
  {
    quote: "AMS transformed our company holiday party. Zero stress on our end—they handled everything from setup to checkout. Our employees loved the unique shopping experience.",
    author: "Jennifer K.",
    role: "HR Director, Tech Company",
  },
  {
    quote: "We've hosted three AMS markets at our winery and each one brings in new customers who stay for wine. It's a perfect partnership.",
    author: "Michael R.",
    role: "Winery Owner",
  },
];

export default function HomePage() {
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [featuredEvents, setFeaturedEvents] = useState([]);

  // ISO date-only strings ("YYYY-MM-DD") get parsed by `new Date(...)` as UTC
  // midnight, which then rolls back a day in every US timezone when formatted.
  // Parse the parts manually so the date matches exactly what admins entered.
  const parseISODateLocal = (s) => {
    if (!s || typeof s !== 'string') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (!m) return new Date(s);
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  };

  useEffect(() => {
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (cancelled || !Array.isArray(data)) return;
        // Featured first; if none flagged featured, fall back to the 3 newest published.
        const flagged = data.filter((e) => e.featured);
        setFeaturedEvents((flagged.length ? flagged : data).slice(0, 3));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/newsletter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newsletterEmail }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.message || 'Thank you for subscribing!');
        setNewsletterEmail('');
      } else {
        toast.error(data.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      toast.error('Failed to subscribe. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="page-transition" data-testid="home-page">
      <SEOHead page="home" />

      {/* Festival of Trees — call for tree sponsors */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: "#FEF6DA", color: "#1E3227" }}
        data-testid="fot-donate-banner"
      >
        {/* subtle radial glow so the banner catches the eye without shouting */}
        <div
          className="absolute inset-0 opacity-60 pointer-events-none"
          style={{
            background:
              "radial-gradient(circle at 20% 50%, rgba(216, 138, 92, 0.20) 0%, transparent 55%)",
          }}
          aria-hidden="true"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 md:py-5 flex flex-col md:flex-row items-center gap-4 md:gap-6">
          <motion.div
            className="hidden md:inline-flex relative items-center justify-center w-12 h-12 rounded-full flex-shrink-0"
            style={{ backgroundColor: "rgba(30, 50, 39, 0.10)", color: "#1E3227" }}
            animate={{ rotate: [-6, 6, -6] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            data-testid="fot-donate-banner-icon"
          >
            <TreePine className="w-6 h-6" fill="#1E3227" fillOpacity={0.12} />
            {/* Twinkling star on top of the tree — punctuates the Christmas feel. */}
            <motion.span
              className="absolute -top-1 -right-1"
              style={{ color: "#C99A3B" }}
              animate={{ scale: [0.7, 1.15, 0.7], opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
              aria-hidden="true"
            >
              <Sparkles className="w-3.5 h-3.5" fill="currentColor" />
            </motion.span>
          </motion.div>
          <div className="flex-1 text-center md:text-left">
            <div
              className="text-sm md:text-base font-bold uppercase tracking-[0.22em]"
              style={{ color: "#8F3E22" }}
            >
              Now accepting submissions
            </div>
            <div
              className="mt-1 font-serif text-lg md:text-2xl leading-snug"
              style={{ color: "#1E3227" }}
            >
              Don&apos;t miss your chance to donate a tree for the Festival of Trees.
            </div>
          </div>
          <Link
            to="/festival-of-trees/donate-a-tree"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white font-medium text-sm transition-colors flex-shrink-0 hover:brightness-110"
            style={{ backgroundColor: "#C86B3E" }}
            data-testid="fot-donate-banner-cta"
          >
            Learn more
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Hero Section - Conversion Focused */}
      <section className="relative py-20 md:py-28 bg-gradient-to-br from-primary/5 via-background to-accent/5 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Star className="w-4 h-4" fill="currentColor" />
                <span>2024 Best of Loudoun Winner</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-foreground leading-tight">
                Turn Your Craft Into a{' '}
                <span className="text-primary">Profitable Business</span>
              </h1>
              
              <p className="mt-6 text-xl text-muted-foreground max-w-lg">
                Find events, price your products with confidence, and grow your artisan business with AMS Compass.
              </p>
              
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href="https://www.madeartisanmarkets.com" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="w-full sm:w-auto rounded-full px-8 py-6 text-lg" data-testid="hero-trial-btn">
                    <Sparkles className="w-5 h-5 mr-2" />
                    Start Free 7-Day Trial
                  </Button>
                </a>
                <Link to="/events">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto rounded-full px-8 py-6 text-lg" data-testid="hero-event-btn">
                    Book an Artisan Market
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
              
              <p className="mt-4 text-sm text-muted-foreground">
                No credit card required • Cancel anytime
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="relative"
            >
              <div className="bg-white rounded-2xl shadow-2xl p-6 border border-border">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <span className="text-sm text-muted-foreground ml-2">AMS Compass Dashboard</span>
                </div>
                <div className="space-y-4">
                  <div className="bg-primary/5 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium">Monthly Profit</span>
                      <span className="text-emerald-600 text-sm font-medium">+23%</span>
                    </div>
                    <div className="text-3xl font-bold text-foreground">$4,280</div>
                    <div className="mt-2 h-2 bg-emerald-100 rounded-full overflow-hidden">
                      <div className="h-full w-3/4 bg-emerald-500 rounded-full"></div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-muted/50 rounded-xl p-4">
                      <div className="text-sm text-muted-foreground">Events This Month</div>
                      <div className="text-2xl font-bold">4</div>
                    </div>
                    <div className="bg-muted/50 rounded-xl p-4">
                      <div className="text-sm text-muted-foreground">Avg. Per Event</div>
                      <div className="text-2xl font-bold">$1,070</div>
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-medium text-amber-900">Pricing Insight</div>
                        <div className="text-sm text-amber-700">Your candles are priced 15% below market. Consider raising to $24.</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/* Floating elements */}
              <div className="absolute -top-4 -right-4 bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                +$1,200 this week
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ecosystem Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="ecosystem-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-foreground">
              Everything You Need to Succeed as an Artisan
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-3xl mx-auto">
              AMS is a complete ecosystem for artisans: tools and knowledge to grow your business, community events where you can build a raving fan base, and technology which can finally work for your unique business needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* AMS Events */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0 }}
              className="bg-gradient-to-br from-primary/5 to-primary/10 rounded-2xl p-8 border border-primary/20"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center mb-6">
                <Calendar className="w-7 h-7 text-primary" />
              </div>
              <h3 className="text-2xl font-serif font-semibold mb-3">AMS Events</h3>
              <p className="text-muted-foreground mb-6">
                Sell your work at curated, high-quality markets. We handle the logistics—you focus on creating and selling.
              </p>
              <Link to="/vendors">
                <Button variant="outline" className="rounded-full" data-testid="eco-events-cta">
                  Apply to Sell
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>

            {/* AMS University */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="bg-gradient-to-br from-accent/5 to-accent/10 rounded-2xl p-8 border border-accent/20"
            >
              <div className="w-14 h-14 rounded-xl bg-accent/20 flex items-center justify-center mb-6">
                <GraduationCap className="w-7 h-7 text-accent" />
              </div>
              <h3 className="text-2xl font-serif font-semibold mb-3">AMS University</h3>
              <p className="text-muted-foreground mb-6">
                Learn how to grow your business with courses on pricing, marketing, booth design, and more.
              </p>
              <Link to="/university">
                <Button variant="outline" className="rounded-full" data-testid="eco-university-cta">
                  Start Learning
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>

            {/* AMS Compass */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-2xl p-8 border-2 border-emerald-300 relative overflow-hidden"
            >
              <div className="absolute top-4 right-4 bg-emerald-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                Most Popular
              </div>
              <div className="w-14 h-14 rounded-xl bg-emerald-200 flex items-center justify-center mb-6">
                <BarChart3 className="w-7 h-7 text-emerald-700" />
              </div>
              <h3 className="text-2xl font-serif font-semibold mb-3">AMS Compass</h3>
              <p className="text-muted-foreground mb-6">
                Use data to price your products profitably, plan your calendar of events, and track your business performance.
              </p>
              <a href="https://www.madeartisanmarkets.com" target="_blank" rel="noopener noreferrer">
                <Button className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="eco-compass-cta">
                  <Sparkles className="mr-2 h-4 w-4" />
                  Start Free Trial
                </Button>
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="py-20 md:py-28 bg-muted/30" data-testid="problem-solution-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Problem Side */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-red-500 font-medium">Sound Familiar?</span>
              <h2 className="text-3xl md:text-4xl font-serif font-semibold text-foreground mt-2 mb-8">
                The Struggles Every Artisan Knows
              </h2>
              
              <div className="space-y-6">
                {[
                  {
                    icon: DollarSign,
                    title: "Underpricing Your Work",
                    description: "You spend hours creating beautiful products, then price them too low because you're not sure what customers will pay.",
                  },
                  {
                    icon: HelpCircle,
                    title: "Choosing the Wrong Events",
                    description: "You pay booth fees, spend a weekend away from family, and come home with barely enough to cover gas money.",
                  },
                  {
                    icon: TrendingDown,
                    title: "Inconsistent Income",
                    description: "Some months are great, others are crickets. You can't plan or grow because you never know what's coming.",
                  },
                ].map((problem, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center flex-shrink-0">
                      <problem.icon className="w-6 h-6 text-red-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">{problem.title}</h3>
                      <p className="text-muted-foreground">{problem.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Solution Side */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-8 shadow-lg border border-border"
            >
              <span className="text-emerald-600 font-medium">The Solution</span>
              <h3 className="text-2xl font-serif font-semibold text-foreground mt-2 mb-6">
                AMS Compass Helps You:
              </h3>
              
              <div className="space-y-4 mb-8">
                {[
                  "Price products based on real market data, not guesswork",
                  "Find events that match your products and profit goals",
                  "Track every sale and see what's actually making money",
                  "Make smarter decisions with business insights",
                  "Connect with a community of successful artisans",
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                    <span className="text-foreground">{benefit}</span>
                  </div>
                ))}
              </div>

              <a href="https://www.madeartisanmarkets.com" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="w-full rounded-full" data-testid="solution-cta">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start Your Free 7-Day Trial
                </Button>
              </a>
              <p className="text-center text-sm text-muted-foreground mt-3">
                No credit card required
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Event Booking Section (B2B) */}
      <section className="py-20 md:py-28 bg-[#0F4C3A] text-white" data-testid="event-booking-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-amber-300 font-medium">For Event Hosts</span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-white mt-2 mb-6">
                Bring an Artisan Market to Your Event
              </h2>
              <p className="text-lg text-white/80 mb-8">
                Want a unique, turnkey experience for your venue, company event, or community gathering? We bring 100+ vetted artisans, professional setup, and a complete checkout system—you just provide the space.
              </p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Users, text: "100+ vetted artisans" },
                  { icon: Truck, text: "Full setup & breakdown" },
                  { icon: Building2, text: "Any venue size" },
                  { icon: PartyPopper, text: "Memorable experiences" },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3 bg-white/10 rounded-xl px-4 py-3">
                    <item.icon className="w-5 h-5 text-amber-300" />
                    <span className="text-white/90">{item.text}</span>
                  </div>
                ))}
              </div>

              <Link to="/events">
                <Button size="lg" className="rounded-full bg-amber-500 hover:bg-amber-400 text-[#0F4C3A] font-semibold" data-testid="book-event-cta">
                  Book an Event
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
                <h3 className="text-xl font-semibold mb-6">How It Works</h3>
                <div className="space-y-6">
                  {[
                    { step: "01", title: "Tell us about your event", desc: "Share details about your venue, date, and goals" },
                    { step: "02", title: "We curate the perfect selection", desc: "Hand-picked artisans matched to your audience" },
                    { step: "03", title: "We handle everything", desc: "Setup, sales, checkout, and cleanup—done" },
                  ].map((item, index) => (
                    <div key={index} className="flex gap-4">
                      <div className="text-3xl font-bold text-amber-300">{item.step}</div>
                      <div>
                        <h4 className="font-semibold text-white">{item.title}</h4>
                        <p className="text-white/70 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Events Section */}
      {featuredEvents.length > 0 && (
        <section
          className="py-20 md:py-28 bg-[color:var(--ed-cream,#FBF6EC)] relative"
          data-testid="featured-events-section"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-2xl mb-14"
            >
              <span className="text-xs uppercase tracking-[0.28em] text-primary font-semibold">
                Save the date
              </span>
              <h2 className="mt-3 text-4xl md:text-5xl font-serif font-medium leading-[1.05]">
                Events we&apos;re hosting next
              </h2>
              <p className="mt-4 font-body text-lg text-muted-foreground max-w-xl">
                Hand-picked artisans, warm details, and community gatherings across the season.
                Tap any event for the full story.
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredEvents.map((ev, idx) => (
                <motion.div
                  key={ev.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                >
                  <Link
                    to={`/events/${ev.slug}`}
                    className="group block bg-white rounded-2xl overflow-hidden border border-secondary/10 hover:border-primary/40 hover:shadow-xl transition-all duration-300 h-full"
                    data-testid={`featured-event-${ev.slug}`}
                  >
                    {ev.hero_image_url ? (
                      <div className="relative h-56 overflow-hidden">
                        <img
                          src={ev.hero_image_url}
                          alt={ev.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                        />
                        {ev.featured && (
                          <span className="absolute top-4 left-4 bg-primary text-white px-3 py-1 text-xs rounded-full font-semibold tracking-wide uppercase">
                            Featured
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="h-56 bg-gradient-to-br from-primary/10 to-secondary/10 flex items-center justify-center">
                        <Calendar className="w-12 h-12 text-primary/40" />
                      </div>
                    )}
                    <div className="p-6">
                      {ev.tagline && (
                        <span className="text-xs uppercase tracking-[0.24em] text-primary/80 font-semibold">
                          {ev.tagline}
                        </span>
                      )}
                      <h3 className="mt-2 text-2xl font-serif leading-tight group-hover:text-primary transition-colors">
                        {ev.title}
                      </h3>
                      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        {ev.start_date && (
                          <span className="inline-flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" />
                            {parseISODateLocal(ev.start_date).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </span>
                        )}
                        {(ev.venue || ev.location) && (
                          <span className="inline-flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5" />
                            {[ev.venue, ev.location].filter(Boolean).join(' · ')}
                          </span>
                        )}
                      </div>
                      <div className="mt-5 inline-flex items-center gap-1 text-primary font-semibold text-sm">
                        Read the story
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="mt-12 text-center">
              <Link
                to="/happenings"
                className="inline-flex items-center gap-2 text-primary hover:text-primary/70 font-semibold transition-colors"
                data-testid="featured-events-see-all"
              >
                See all events
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Social Proof Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-foreground">
              Artisans Are Growing With AMS
            </h2>
            <p className="mt-4 text-lg text-muted-foreground">
              Join 100+ artisans who've transformed their businesses
            </p>
          </motion.div>

          {/* Artisan Testimonials */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {artisanTestimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-muted/30 rounded-2xl p-6 border border-border"
              >
                {testimonial.metric && (
                  <div className="inline-block bg-emerald-100 text-emerald-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
                    {testimonial.metric}
                  </div>
                )}
                <p className="text-foreground mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <span className="font-semibold text-primary">{testimonial.author[0]}</span>
                  </div>
                  <div>
                    <div className="font-semibold text-foreground">{testimonial.author}</div>
                    <div className="text-sm text-muted-foreground">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "100+", label: "Artisan Members" },
              { value: "50+", label: "Events Hosted" },
              { value: "4", label: "Years of Experience" },
              { value: "$150K", label: "Artisan Sales Generated" },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-4xl md:text-5xl font-bold text-primary">{stat.value}</div>
                <div className="text-muted-foreground mt-1">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Sell With Us Section */}
      <section className="py-20 md:py-28 bg-muted/30" data-testid="sell-with-us-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-foreground">
              Ready to Sell at Our Events?
            </h2>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Join our community of artisans and get access to curated, high-quality selling opportunities. 
              <strong className="text-foreground"> Pro tip:</strong> AMS Compass members get priority placement at events.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/vendors">
                <Button size="lg" variant="outline" className="rounded-full px-8" data-testid="apply-vendor-cta">
                  Apply as a Vendor
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="https://www.madeartisanmarkets.com" target="_blank" rel="noopener noreferrer">
                <Button size="lg" className="rounded-full px-8" data-testid="sell-trial-cta">
                  <Sparkles className="w-5 h-5 mr-2" />
                  Start Free Trial + Get Priority
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Email Capture Section */}
      <section className="py-20 md:py-28 bg-primary text-white" data-testid="email-capture-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-serif font-semibold text-white">
              Get Tips to Grow Your Artisan Business
            </h2>
            <p className="mt-4 text-lg text-white/80 max-w-2xl mx-auto mb-8">
              Join our newsletter for weekly insights on pricing, events, marketing, and building a profitable craft business.
            </p>
            
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                disabled={isSubmitting}
                className="flex-1 px-6 py-3 rounded-full text-foreground bg-white focus:outline-none focus:ring-2 focus:ring-amber-300 disabled:opacity-50"
                data-testid="newsletter-email"
              />
              <Button 
                type="submit" 
                disabled={isSubmitting}
                className="rounded-full bg-amber-500 hover:bg-amber-400 text-[#0F4C3A] font-semibold px-8 disabled:opacity-50" 
                data-testid="newsletter-submit"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Subscribing...
                  </>
                ) : (
                  'Subscribe'
                )}
              </Button>
            </form>
            <p className="text-sm text-white/60 mt-4">
              No spam. Unsubscribe anytime.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-semibold text-foreground">
              Stop Guessing. Start Profiting.
            </h2>
            <p className="mt-4 text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
              Build a real business, not just a hobby. Start your free 7-day trial of AMS Compass today.
            </p>
            
            <a href="https://www.madeartisanmarkets.com" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="rounded-full px-10 py-6 text-lg" data-testid="final-trial-cta">
                <Sparkles className="w-5 h-5 mr-2" />
                Start Free 7-Day Trial
              </Button>
            </a>
            <p className="text-sm text-muted-foreground mt-4">
              No credit card required • Cancel anytime
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
